// AudioSuite (Pro Tools Kap. „AudioSuite Plug-ins“): Clips offline bearbeiten, das Ergebnis wird eine neue Audiodatei.
// Nicht zerstörend im Sinne von Rückgängig: der Clip zeigt danach auf die neue Datei, ⌘Z holt die alte zurück.
// Die neue Datei wird hochgeladen, damit alle im Raum sie hören.
import type { Clip, InsertSlot, Project } from '@srs/shared';
import { engine } from './engine.ts';
import { resolveElastic, runJob, elasticReady } from './elastic.ts';
import { Chain } from './mixgraph.ts';
import type { StretchMode } from './stretch.ts';

export type SuiteOp =
  | { kind: 'reverse' }
  | { kind: 'invert' }
  | { kind: 'dc' }
  | { kind: 'pitch'; semitones: number; mode: StretchMode }
  | { kind: 'stretch'; ratio: number; mode: StretchMode }
  | { kind: 'varispeed'; ratio: number }
  | { kind: 'plugin'; slot: InsertSlot; tail: number };

export const SUITE_OPS: { id: SuiteOp['kind']; label: string; hint: string }[] = [
  { id: 'pitch', label: 'Tonhöhe ändern', hint: 'Halbtöne und Cent, die Länge bleibt' },
  { id: 'stretch', label: 'Zeit dehnen', hint: 'Länge oder Tempo ändern, die Tonhöhe bleibt' },
  { id: 'varispeed', label: 'Varispeed', hint: 'Schneller und höher bzw. langsamer und tiefer, wie ein Bandgerät' },
  { id: 'plugin', label: 'Plugin anwenden', hint: 'Ein Insert der Spur fest ins Audio rechnen, mit Nachhall' },
  { id: 'reverse', label: 'Umkehren', hint: 'Rückwärts abspielen (z. B. für Reverse-Effekte)' },
  { id: 'invert', label: 'Phase invertieren', hint: 'Polarität drehen, z. B. bei Snare oben/unten' },
  { id: 'dc', label: 'DC-Offset entfernen', hint: 'Gleichspannungsanteil entfernen, verhindert Knackser an Schnitten' },
];

/** Audio, wie der Clip es spielt (ohne Clip Gain und Fades): Ausschnitt der Quelle bzw. der Elastic-Fassung */
export async function clipAudio(p: Project, c: Clip, a = c.start, b = c.start + c.duration): Promise<{ channels: Float32Array[]; sampleRate: number } | null> {
  await elasticReady(p);
  const el = resolveElastic(p, c);
  const id = el && el !== 'pending' ? el.id : c.sourceId, buf = id ? engine.buffers.get(id) : undefined;
  if (!buf) return null;
  const sr = buf.sampleRate, from = (el && el !== 'pending' ? el.src0 : c.offset) + (a - c.start);
  const i0 = Math.max(0, Math.round(from * sr)), n = Math.max(1, Math.round((b - a) * sr));
  return { sampleRate: sr, channels: Array.from({ length: buf.numberOfChannels }, (_, k) => { const d = new Float32Array(n); d.set(buf.getChannelData(k).subarray(i0, Math.min(buf.length, i0 + n))); return d; }) };
}

/** Eine Bearbeitung rechnen */
export async function processAudio(input: { channels: Float32Array[]; sampleRate: number }, op: SuiteOp, bpm: number): Promise<{ channels: Float32Array[]; sampleRate: number }> {
  const { channels, sampleRate: sr } = input, len = channels[0].length;
  switch (op.kind) {
    case 'reverse': return { sampleRate: sr, channels: channels.map((c) => c.slice().reverse()) };
    case 'invert': return { sampleRate: sr, channels: channels.map((c) => c.map((v) => -v)) };
    case 'dc': return { sampleRate: sr, channels: channels.map((c) => {
      // Hochpass 1. Ordnung bei ~5 Hz entfernt den Gleichanteil, ohne den Klang zu ändern
      const out = new Float32Array(len), R = 1 - (2 * Math.PI * 5) / sr; let x1 = 0, y1 = 0;
      for (let i = 0; i < len; i++) { const y = c[i] - x1 + R * y1; x1 = c[i]; y1 = y; out[i] = y; }
      return out;
    }) };
    case 'pitch': return { sampleRate: sr, channels: await runJob({ channels: channels.map((c) => c.slice()), sampleRate: sr, points: [{ out: 0, src: 0 }, { out: len, src: len }], outLen: len, mode: op.mode, semitones: op.semitones }) };
    case 'stretch': { const outLen = Math.max(1, Math.round(len * op.ratio)); return { sampleRate: sr, channels: await runJob({ channels: channels.map((c) => c.slice()), sampleRate: sr, points: [{ out: 0, src: 0 }, { out: outLen, src: len }], outLen, mode: op.mode }) }; }
    case 'varispeed': { const outLen = Math.max(1, Math.round(len / op.ratio)); return { sampleRate: sr, channels: await runJob({ channels: channels.map((c) => c.slice()), sampleRate: sr, points: [{ out: 0, src: 0 }, { out: outLen, src: len }], outLen, mode: 'varispeed' }) }; }
    case 'plugin': {
      const total = len + Math.round(op.tail * sr);
      const off = new OfflineAudioContext(Math.max(2, channels.length), total, sr);
      await off.audioWorklet.addModule('/dsp-worklet.js').catch(() => undefined);
      const b = off.createBuffer(channels.length, len, sr); channels.forEach((c, i) => b.copyToChannel(c as Float32Array<ArrayBuffer>, i));
      const src = off.createBufferSource(); src.buffer = b;
      const chain = new Chain(off); chain.sync([{ ...op.slot, bypass: false }], { bpm, offline: true });
      src.connect(chain.input); chain.output.connect(off.destination); src.start(0);
      const r = await off.startRendering();
      const n = channels.length === 1 ? 1 : r.numberOfChannels;
      return { sampleRate: sr, channels: Array.from({ length: n }, (_, i) => r.getChannelData(i).slice()) };
    }
  }
}

export const toBuffer = (x: { channels: Float32Array[]; sampleRate: number }) => {
  const b = new AudioBuffer({ length: x.channels[0].length, numberOfChannels: x.channels.length, sampleRate: x.sampleRate });
  x.channels.forEach((c, i) => b.copyToChannel(c as Float32Array<ArrayBuffer>, i));
  return b;
};
